import React, { useState, useContext, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useDeliveryService from '../../../app/services/hooks/useDeliveryService';
import useSocietyService from '../../../app/services/hooks/useSocietyService';
import useClientService from '../../../app/services/hooks/useClientService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import useFetch from '../../../hooks/useFetch';
import { DeliveryDataForm, defaultDeliveryDataForm } from '../../../app/models/Delivery';
import { Location } from '../../../app/models/Location';
import { Society } from '../../../app/models/Society';
import { Client } from '../../../app/models/Client';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { EndPoints } from '../../../app/services/EndPoints';

import DeliveryForm from './forms/DeliveryForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import LazyLoading from '../../../components/LazyLoading';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const DeliveryCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();

    const { storeDelivery, fetchingStoreDelivery } = useDeliveryService();
    const { getSocieties } = useSocietyService();
    const { getClients } = useClientService();
    const { doGet } = useFetch();

    // Estados
    const [delivery, setDelivery] = useState<DeliveryDataForm>(defaultDeliveryDataForm);
    const [errorFields, setErrorFields] = useState<Record<string, string[]> | undefined>();
    const [loadingData, setLoadingData] = useState(true);

    // Datos para los selects
    const [salesOrders, setSalesOrders] = useState<any[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [societies, setSocieties] = useState<Society[]>([]);
    const [clients, setClients] = useState<Client[]>([]);

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        loadInitialData();

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const loadInitialData = () => {
        if (showLoading) showLoading('loading', 'Cargando datos...');

        timeoutRef.current = setTimeout(() => {
            setLoadingData(false);
            if (hideLoading) hideLoading();
        }, 5000);

        // Cargar datos iniciales desde el endpoint CREATE_DELIVERY
        doGet({
            url: EndPoints.DELIVERIES.CREATE_DELIVERY,
            onSuccess: (response: ServiceResponse) => {
                setSalesOrders(response.data?.sales_orders || []);
                setLocations(response.data?.locations || []);
                setLoadingData(false);
                if (hideLoading) hideLoading();
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            },
            onError: (error) => {
                console.error('Error al cargar datos iniciales:', error);
                setLoadingData(false);
                if (hideLoading) hideLoading();
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            }
        });

        // Cargar sociedades
        getSocieties(
            {
                page: 1,
                per_page: 1000,
                sort: 'id',
                order: 'asc'
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    setSocieties(response.data?.societies || []);
                },
                onError: (error) => {
                    console.error('Error al cargar sociedades:', error);
                }
            }
        );

        // Cargar clientes
        getClients(
            {
                page: 1,
                per_page: 1000,
                sort: 'id',
                order: 'asc',
                business_name: null
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    setClients(response.data?.clients || []);
                },
                onError: (error) => {
                    console.error('Error al cargar clientes:', error);
                }
            }
        );
    };

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando entrega...');

        storeDelivery(delivery, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                toast.success(response.message, { autoClose: 2500 });

                // Navegar a la vista de la entrega creada
                navigationPage('/deliveries/' + response.data.delivery_id + '/view');

                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    if (loadingData) {
        return (
            <>
                <LazyLoading height="300" />
                <div className="text-center mt-3">
                    <button className="btn btn-secondary" onClick={() => setLoadingData(false)}>
                        Cancelar carga y mostrar formulario
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <DeliveryForm
                        delivery={delivery}
                        setDelivery={setDelivery}
                        salesOrders={salesOrders}
                        locations={locations}
                        societies={societies}
                        clients={clients}
                        errorFields={errorFields}
                        onSalesOrderChange={(salesOrderId) => {
                            // Cuando cambia el pedido, heredar sociedad y cliente
                            if (salesOrderId) {
                                const selectedOrder = salesOrders.find(order => order.id === salesOrderId);
                                if (selectedOrder) {
                                    // Los IDs de sociedad y cliente se heredarán del pedido en el backend
                                    // Aquí solo actualizamos el estado local para mostrar los valores
                                    setDelivery({
                                        ...delivery,
                                        sales_order_id: salesOrderId
                                    });
                                }
                            }
                        }}
                    />
                </div>
            </div>

            <hr />

            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} locked={fetchingStoreDelivery} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStoreDelivery} />
                </div>
            </div>
        </>
    );
};

export default DeliveryCreate;

